'use client'

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { walletApi } from '../api/endpoints'
import CoinGlyph from '../components/lucky/CoinGlyph'
import LuckyIcon from '../components/lucky/LuckyIcon'
import { showToast } from '../components/ui/Toast'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'
import { COIN_TYPES, type CoinMap, type CoinRateCents, type CoinType } from '../types'
import { formatEurCents, quoteCoinExchange, walletValueCents } from '../utils/coinExchange'
import { coinNameKey } from '../utils/helpers'

const CONVERT_ORDER: CoinType[] = ['bronze', 'silver', 'gold', 'diamond']

export default function WalletPage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale, dir } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const queryClient = useQueryClient()

  const [from, setFrom] = useState<CoinType>('bronze')
  const [to, setTo] = useState<CoinType>('silver')
  const [amount, setAmount] = useState('')

  const { data: walletData, isLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getCoins(),
  })

  const { data: rateData, isError: ratesError } = useQuery({
    queryKey: ['wallet', 'rates'],
    queryFn: () => walletApi.getRates(),
  })

  const coins = walletData?.coins || { bronze: 0, silver: 0, gold: 0, diamond: 0 }
  const rates = rateData?.rates
  const amountNum = Number(amount)
  const amountOk = amount.trim() !== '' && Number.isInteger(amountNum) && amountNum > 0

  const quote = useMemo(() => {
    if (!rates || !amountOk || from === to) return null
    return quoteCoinExchange(amountNum, rates[from], rates[to])
  }, [rates, amountOk, amountNum, from, to])

  const totalEur = rates ? formatEurCents(walletValueCents(coins, rates)) : null

  const convertMutation = useMutation({
    mutationFn: () => walletApi.convert(from, to, amountNum),
    onSuccess: (res) => {
      showToast(t('wallet.convertOk', {
        spent: res.spent.toLocaleString(numberLocale),
        from: t(coinNameKey(res.from)),
        receive: res.received.toLocaleString(numberLocale),
        to: t(coinNameKey(res.to)),
      }), 'success')
      setAmount('')
      queryClient.setQueryData(['wallet'], (prev: { coins: CoinMap; dollar: number } | undefined) => (
        prev ? { ...prev, coins: res.coins } : { coins: res.coins, dollar: 0 }
      ))
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      showToast(err.response?.data?.message || t('common.error'), 'error')
    },
  })

  const canConvert = Boolean(
    rates && amountOk && from !== to && quote?.ok && amountNum <= coins[from] && !convertMutation.isPending,
  )

  if (isLoading) {
    return (
      <div className="lucky-wallet mini-app-container pb-20 min-h-screen flex items-center justify-center">
        <div className="lucky-wallet__empty">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="lucky-wallet mini-app-container pb-20 min-h-screen">
      <div className="lucky-wallet__inner">
        <header className="lucky-wallet__header">
          <button
            type="button"
            className="lucky-wallet__back"
            onClick={() => navigate(-1)}
            aria-label={t('common.back')}
          >
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-wallet__header-icon">
            <LuckyIcon name="wallet" size={28} alt="" />
          </div>
          <div>
            <h1>{t('wallet.title')}</h1>
            <p>{t('wallet.subtitle')}</p>
          </div>
        </header>

        <section className="lucky-wallet__card">
          <div className="lucky-wallet__dollar">
            <span>{t('wallet.approxValue')}</span>
            <strong dir="ltr">{totalEur || '—'}</strong>
          </div>
          <div className="lucky-wallet__coins">
            {COIN_TYPES.map((coinType) => (
              <div key={coinType} className="lucky-wallet__coin">
                <CoinGlyph type={coinType} size={28} />
                <span>{t(coinNameKey(coinType))}</span>
                <strong>{coins[coinType].toLocaleString(numberLocale)}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="lucky-wallet__card">
          <h2 className="lucky-wallet__title">{t('wallet.convertTitle')}</h2>
          <p className="lucky-wallet__hint">{t('wallet.convertHint')}</p>
          {ratesError && <p className="lucky-wallet__hint">{t('common.error')}</p>}

          {rates && (
            <div className="lucky-wallet__rates">
              {CONVERT_ORDER.map((coinType) => (
                <span key={coinType} dir="ltr">
                  {t(coinNameKey(coinType))} · {formatEurCents(BigInt(rates[coinType] || 0))}
                </span>
              ))}
            </div>
          )}

          <p className="lucky-wallet__label">{t('wallet.convertFrom')}</p>
          <CoinPick
            selected={from}
            rates={rates}
            onPick={(coin) => {
              setFrom(coin)
              if (coin === to) {
                setTo(CONVERT_ORDER.find((c) => c !== coin) || 'silver')
              }
            }}
          />

          <label className="lucky-wallet__label" htmlFor="convert-amount">{t('wallet.convertAmount')}</label>
          <input
            id="convert-amount"
            className="lucky-wallet__input"
            inputMode="numeric"
            pattern="[0-9]*"
            value={amount}
            placeholder="0"
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
          />
          <p className="lucky-wallet__hint">
            {t('wallet.convertBalance', { n: coins[from].toLocaleString(numberLocale) })}
          </p>

          <p className="lucky-wallet__label">{t('wallet.convertTo')}</p>
          <CoinPick selected={to} rates={rates} disabled={from} onPick={setTo} />

          <ConvertPreview
            from={from}
            to={to}
            amount={amountNum}
            amountOk={amountOk}
            balance={coins[from]}
            quote={quote}
            numberLocale={numberLocale}
          />

          <button
            type="button"
            className="lucky-wallet__cta"
            disabled={!canConvert}
            onClick={() => convertMutation.mutate()}
          >
            {t('wallet.convertSubmit')}
          </button>
        </section>

        <section className="lucky-wallet__card">
          <h2 className="lucky-wallet__title">{t('wallet.cashTitle')}</h2>
          <p className="lucky-wallet__hint">{t('wallet.cashHint')}</p>
          <button type="button" className="lucky-wallet__cta" onClick={() => navigate('/cash-out')}>
            {t('wallet.goCashOut')}
          </button>
        </section>
      </div>
    </div>
  )
}

function CoinPick({
  selected,
  rates,
  disabled,
  onPick,
}: {
  selected: CoinType
  rates?: CoinRateCents
  disabled?: CoinType
  onPick: (coin: CoinType) => void
}) {
  const t = useT()
  return (
    <div className="lucky-wallet__pick">
      {CONVERT_ORDER.map((coin) => (
        <button
          key={coin}
          type="button"
          className={`lucky-wallet__pick-btn${selected === coin ? ' is-on' : ''}`}
          disabled={disabled === coin}
          onClick={() => onPick(coin)}
        >
          <CoinGlyph type={coin} size={22} />
          <span>{t(coinNameKey(coin))}</span>
          {rates && <small dir="ltr">{formatEurCents(BigInt(rates[coin] || 0))}</small>}
        </button>
      ))}
    </div>
  )
}

function ConvertPreview({
  from,
  to,
  amount,
  amountOk,
  balance,
  quote,
  numberLocale,
}: {
  from: CoinType
  to: CoinType
  amount: number
  amountOk: boolean
  balance: number
  quote: ReturnType<typeof quoteCoinExchange>
  numberLocale: string
}) {
  const t = useT()
  if (from === to) {
    return <p className="lucky-wallet__preview">{t('wallet.convertSame')}</p>
  }
  if (!amountOk || !quote) return null
  if (amount > balance) {
    return <p className="lucky-wallet__preview">{t('wallet.convertNeedFunds')}</p>
  }
  if (!quote.ok) {
    return (
      <p className="lucky-wallet__preview">
        {t('wallet.convertTooSmall', {
          min: quote.minAmount.toLocaleString(numberLocale),
          from: t(coinNameKey(from)),
          to: t(coinNameKey(to)),
        })}
      </p>
    )
  }
  return (
    <p className="lucky-wallet__preview">
      {t('wallet.convertValue', {
        amount: amount.toLocaleString(numberLocale),
        coin: t(coinNameKey(from)),
        eur: formatEurCents(quote.valueCents),
      })}
      <br />
      {t('wallet.convertResult', {
        receive: quote.receive.toLocaleString(numberLocale),
        to: t(coinNameKey(to)),
        leftover: quote.leftover.toLocaleString(numberLocale),
        from: t(coinNameKey(from)),
      })}
    </p>
  )
}
